import { useState } from "react";
import { useLocation } from "wouter";
import { useAuth } from "@/hooks/use-auth";
import DeleteAccountConfirmationDialog from "@/pages/delete-account-confirmation-dialog";
import {
  LogOut,
  Menu,
  UserRoundX,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { UserProfileMenu } from "@/components/ui/user-profile-menu";


export function MobileNav() {
  const { user, logoutMutation, deleteAccountMutation } = useAuth();
  const [, navigate] = useLocation();
  const [open, setOpen] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [desktopMenuOpen, setDesktopMenuOpen] = useState(false);

  const handleLogout = () => {
    logoutMutation.mutate();
    setOpen(false);
    // Navigate to landing page
    navigate("/landing-page");
    // Fallback hard redirect to ensure server-side session cleared
    setTimeout(() => {
      window.location.href = "/landing-page";
    }, 100);
  };

  const handleDeleteAccount = () => {
    setShowDeleteDialog(true);
    setOpen(false);
  };

  const confirmDeleteAccount = () => {
    deleteAccountMutation.mutate();
    setOpen(false);
    setShowDeleteDialog(false);
    // Navigate to landing page
    navigate("/landing-page");
    // Fallback hard redirect
    setTimeout(() => {
      window.location.href = "/landing-page";
    }, 100);
  };

  const handleCancelDelete = () => {
    setShowDeleteDialog(false);
    setOpen(true);
  };

  const getInitials = (firstName?: string, lastName?: string) => {
    return `${firstName?.[0] || ""}${lastName?.[0] || ""}`.toUpperCase();
  };

  return (
    <>
      <header className="bg-white md:hidden bg-white border-b-0 px-4 py-5 items-center  sticky top-0 z-10 shadow-none h-[76px]">
        <div className="flex items-center nav-btn">
          <Button
            variant="ghost"
            size="sm"
            className="text-[#6CACAA] focus:outline-none h-8 w-8 p-0"
            onClick={() => setOpen(true)}
          >
            <Menu className="h-5 w-5" />
          </Button>
          <span className="ml-2 font-semibold text-[#304145]">
            Client Portal
          </span>
        </div>

        <div className="flex items-center space-x-1">
          

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button className="hidden">
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent
              style={{ position: "fixed", zIndex: "99999" }}
              side="left"
              className="p-0"
            >
              <div className="bg-[#4c7375] text-white p-4">
                <div className="flex items-center">
                  <Avatar className="h-12 w-12 border-2 border-white">
                    <AvatarFallback className="bg-primary-dark">
                      {getInitials(user?.firstName, user?.lastName)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="ml-3">
                    <p className="font-medium">
                      {user?.firstName} {user?.lastName}
                    </p>
                    <p className="text-xs opacity-80">{user?.email}</p>
                   
                  </div>
                </div>
              </div>

              <ScrollArea className="h-[calc(100vh-130px)]">
                <div className="p-2">

                  <div className="">
                    <Button
                      variant="ghost"
                      className="w-full flex items-center justify-start px-4 text-red-600 hover:bg-red-50 hover:text-red-700"
                      onClick={handleDeleteAccount}
                    >
                      <UserRoundX className="mr-3 h-5 w-5" />
                      Delete my account
                    </Button>
                  </div>

                  <div className="">
                    <Button
                      variant="ghost"
                      className="w-full flex items-center justify-start px-4 text-red-600 hover:bg-red-50 hover:text-red-700"
                      onClick={handleLogout}
                    >
                      <LogOut className="mr-3 h-5 w-5" />
                      Log out
                    </Button>
                  </div>
                </div>
              </ScrollArea>
            </SheetContent>
          </Sheet>
        </div>
        <hr
          style={{ color: "#b4dbdb" }}
          className="text-[#B4DBD8] h-[1.19px] mt-3"
        />
      </header>

      {/* Desktop Header */}
      <header className="hidden md:flex bg-white border-b-0 px-6 py-4 items-center sticky top-0 z-10 shadow-none h-[76px]">
        <div className="flex-1">
          <span className="font-semibold text-[#304145]">Client Portal</span>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            className="text-[#6CACAA] focus:outline-none h-8 px-2"
            onClick={() => setDesktopMenuOpen(true)}
            aria-label="Open user menu"
          >
            <div className="flex items-center gap-2">
              <Avatar className="h-8 w-8 border border-[#b4dbdb]">
                <AvatarFallback className="bg-primary-light/30 text-[#304145]">
                  {getInitials(user?.firstName, user?.lastName)}
                </AvatarFallback>
              </Avatar>
              <span className="text-[#304145] hidden lg:inline">{user?.firstName} {user?.lastName}</span>
            </div>
          </Button>
        </div>
      </header>

      {/* Confirmation Dialog */}
      <DeleteAccountConfirmationDialog
        isOpen={showDeleteDialog}
        onConfirm={confirmDeleteAccount}
        onCancel={handleCancelDelete}
      />

      {/* Desktop User Profile Menu Overlay */}
      <UserProfileMenu open={desktopMenuOpen} onClose={() => setDesktopMenuOpen(false)} />

      <div className="md:hidden md:h-0"></div>
    </>
  );
}
